import { routePaths } from "../routes/routePaths";

// A logged-out visitor of a private page (an email link opened in a new browser, an expired
// session) is sent to /login?returnUrl=<that page>, and comes back to it after logging in.

const RETURN_URL_PARAM = "returnUrl";

/** Only used to parse a relative path; never navigated to. */
const PARSE_BASE = "https://return-url.invalid";

/** One "/" and then not a second one or a "\": "//evil.com" and "/\evil.com" lead off the site. */
const SITE_PATH_START = /^\/(?![/\\])/;

/** Backslashes (read as "/") and control characters (tabs and newlines are dropped by browsers). */
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARACTERS = /[\\\u0000-\u001F\u007F]/;

/** Coming back to these would show the login form again or log the user straight out. */
const NON_RETURNABLE_PATHS = [routePaths.login, routePaths.logout];

/** The login page that brings the visitor back to `pathAndSearch` once they are logged in. */
export const buildLoginPathWithReturnUrl = (pathAndSearch: string): string =>
  `${routePaths.login}?${RETURN_URL_PARAM}=${encodeURIComponent(pathAndSearch)}`;

/**
 * The page to go to after login, or undefined when `value` is not a page of this site. The
 * result is assigned to window.location.href, so anything else would be an open redirect.
 */
export const getSafeReturnUrl = (value: string | null | undefined): string | undefined => {
  if (!value || !SITE_PATH_START.test(value) || UNSAFE_CHARACTERS.test(value)) {
    return undefined;
  }

  let url: URL;
  try {
    url = new URL(value, PARSE_BASE);
  } catch {
    return undefined;
  }

  // What the browser makes of it must stay on this site, whatever slipped past the checks above.
  if (url.origin !== PARSE_BASE) {
    return undefined;
  }

  // Routes match decoded ("/%6Cogout" is /logout), case-insensitively and with a trailing "/";
  // dot segments are already resolved.
  let pathname: string;
  try {
    pathname = decodeURIComponent(url.pathname).replace(/\/+$/, "").toLowerCase();
  } catch {
    return undefined;
  }
  if (NON_RETURNABLE_PATHS.includes(pathname)) {
    return undefined;
  }

  // Resolving dot segments can leave "//" again: "/.//evil.com" and "/x/..//evil.com" become
  // "//evil.com", which leads off the site.
  const result = `${url.pathname}${url.search}${url.hash}`;
  return SITE_PATH_START.test(result) ? result : undefined;
};
